import { atom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import axios from 'axios'
import config from '../../Config'

// User atoms
// getOnInit: true — sin esto, jotai arranca cada atomWithStorage con su valor
// por defecto (false/[]/'') en el primer render y recién después de montar lee
// localStorage y actualiza, lo que hace que RouteCheck decida una vez con sesión
// "vacía" y otra vez (mal sincronizada entre atoms) con la real, causando el
// parpadeo hacia /client/complete-profile en sesiones ya logueadas.
export const isAuthAtom = atomWithStorage('isAuth', false, undefined, { getOnInit: true })
export const dataUserAtom = atomWithStorage('dataUser', [], undefined, { getOnInit: true })
export const authUserAtom = atomWithStorage('authUser', [], undefined, { getOnInit: true })
export const coinsAtom = atomWithStorage('coins', 0)
export const visionUserModeAtom = atomWithStorage('visionUserMode', false)
export const userRoleAtom = atomWithStorage('userRole', '', undefined, { getOnInit: true })

// Overrides de presencia en vivo (userId -> true/false), alimentado por
// PresenceHost al recibir 'presence-changed' del relay. Deliberadamente NO
// persistido (atomWithStorage): es estado transitorio de la sesión, se
// resetea solo en cada carga, y arrancar "en blanco" es correcto (nada
// online hasta que llegue el primer evento o se lea is_live por REST).
// Quien lo lee (ChatScreen, discover, etc.) lo usa solo para pisar el
// snapshot REST (is_live) cuando hay un evento más fresco; si un userId no
// está acá, se sigue confiando en el dato de la API.
export const onlineStatusOverridesAtom = atom(new Map())

// User actions
export const setAuthUserAtom = atom(null, (get, set, payload) => {
  set(authUserAtom, payload)
  set(isAuthAtom, true)
  if (payload && payload.role) {
    set(userRoleAtom, payload.role)
  }
})

export const setDataUserAtom = atom(null, (get, set, payload) => {
  set(dataUserAtom, payload)
  if (payload && payload.role) {
    set(userRoleAtom, payload.role)
  }
})

export const logoutUserAtom = atom(null, (get, set) => {
  // Borra solo el push token de ESTE navegador (push_id: null a secas borraría
  // los tokens de todos los dispositivos del usuario, incluido el teléfono)
  const authUser = get(authUserAtom)
  const localPushId = window.OneSignal?.User?.PushSubscription?.id
  if (authUser?.token && localPushId) {
    axios.post(
      `${config.REACT_APP_BASE_URL}/api/v1/user/pushId`,
      { push_id: null, old_push_id: localPushId },
      { headers: { Authorization: `Bearer ${authUser.token}` } }
    ).catch(() => {})
  }

  set(isAuthAtom, false)
  set(dataUserAtom, [])
  set(authUserAtom, [])
  set(userRoleAtom, '')
  set(visionUserModeAtom, false)
})

export const setCointAtom = atom(null, (get, set, payload) => {
  set(coinsAtom, payload)
})

export const toggleVisionUserModeAtom = atom(null, (get, set) => {
  set(visionUserModeAtom, !get(visionUserModeAtom))
})

// Cart atoms
export const cartAtom = atomWithStorage('cart', [])
export const quantityModalAtom = atom(0)

// Cart actions
export const addToCartAtom = atom(null, (get, set, payload) => {
  const cart = get(cartAtom)
  const itemInCart = cart.find((item) => item.product_id === payload.product_id)
  if (itemInCart) {
    itemInCart.quantity_cart++
    set(cartAtom, [...cart])
  } else {
    set(cartAtom, [...cart, { ...payload, quantity_cart: 1 }])
  }
})

export const incrementQuantityAtom = atom(null, (get, set, productId) => {
  const cart = get(cartAtom)
  const item = cart.find((item) => item.product_id === productId)
  if (item) {
    item.quantity_cart++
    set(cartAtom, [...cart])
  }
})

export const decrementQuantityAtom = atom(null, (get, set, productId) => {
  const cart = get(cartAtom)
  const item = cart.find((item) => item.product_id === productId)
  if (item) {
    if (item.quantity_cart === 1) {
      set(cartAtom, cart.filter((item) => item.product_id !== productId))
    } else {
      item.quantity_cart--
      set(cartAtom, [...cart])
    }
  }
})

export const removeItemAtom = atom(null, (get, set, productId) => {
  const cart = get(cartAtom)
  set(cartAtom, cart.filter((item) => item.product_id !== productId))
})

export const removeAllItemsAtom = atom(null, (get, set) => {
  set(cartAtom, [])
})

// Billing atoms
export const billingAtom = atomWithStorage('billing', [])
export const setBillingAtom = atom(null, (get, set, payload) => {
  set(billingAtom, payload)
})

// Date atoms
export const datetimeAtom = atomWithStorage('datetime', [])
export const setDateTimeAtom = atom(null, (get, set, payload) => {
  set(datetimeAtom, payload)
})

// Gallery atoms
export const imagesAtom = atomWithStorage('images', [])
export const setImagesAtom = atom(null, (get, set, payload) => {
  set(imagesAtom, payload)
})
export const resetGalleryAtom = atom(null, (get, set) => {
  set(imagesAtom, [])
})

// Temporary atoms
export const temporaryPaymentSelectedAtom = atomWithStorage('temporaryPaymentSelected', [])
export const temporarySelectionOtherBankAtom = atomWithStorage('temporarySelectionOtherBank', false)
export const temporarySelectPositiveBalanceAtom = atomWithStorage('temporarySelectPositiveBalance', false)
export const temporaryInfoReferencePaymentAtom = atomWithStorage('temporaryInfoReferencePayment', null)
export const temporaryInitDashboardAtom = atomWithStorage('temporaryInitDashboard', false)

export const setTemporaryPaymentSelectedAtom = atom(null, (get, set, payload) => {
  set(temporaryPaymentSelectedAtom, payload)
})

export const setTemporarySelectionOtherBankAtom = atom(null, (get, set, payload) => {
  set(temporarySelectionOtherBankAtom, payload)
})

export const setTemporarySelectPositiveBalanceAtom = atom(null, (get, set, payload) => {
  set(temporarySelectPositiveBalanceAtom, payload)
})

export const setTemporaryInfoReferencePaymentAtom = atom(null, (get, set, payload) => {
  set(temporaryInfoReferencePaymentAtom, payload)
})

export const setTemporaryInitDashboardAtom = atom(null, (get, set, payload) => {
  set(temporaryInitDashboardAtom, payload)
})

export const resetTemporaryAtom = atom(null, (get, set) => {
  set(temporaryPaymentSelectedAtom, [])
  set(temporarySelectionOtherBankAtom, false)
  set(temporarySelectPositiveBalanceAtom, false)
  set(temporaryInfoReferencePaymentAtom, null)
  set(temporaryInitDashboardAtom, false)
})

// Cache atoms for models list (persist in memory across route changes)
export const modelsListAtom = atom([])
export const modelsPageAtom = atom(1)
export const modelsHasMoreAtom = atom(true)
export const modelsSearchAtom = atom('')
export const modelsScrollYAtom = atom(0)

// Cache atoms for packs list
export const packsListAtom = atom([])
export const packsPageAtom = atom(1)
export const packsHasMoreAtom = atom(true)
export const packsSearchAtom = atom('')
export const packsScrollYAtom = atom(0)

// Cache atoms for feed list
export const feedListAtom = atom([])
export const feedTabAtom = atom('all')
export const feedNextPageUrlAtom = atom(null)
export const feedScrollYAtom = atom(0)

// Global scroll position registry (keyed by pathname)
export const scrollPositionsAtom = atom({})

// Sidebar atoms (Jotai - no re-renders en todo el árbol)
export const sidebarOpenAtom = atom(true)
export const sidebarOpenMobileAtom = atom(false)

// Live streaming — oculta el header global durante transmisiones
export const hideHeaderAtom = atom(false)

// Dark mode
export const darkModeAtom = atomWithStorage('darkMode', true)
export const toggleDarkModeAtom = atom(null, (get, set) => {
  set(darkModeAtom, !get(darkModeAtom))
})
